import React from "react";
import { Clock, Layers, Users } from "react-feather";
import { formatDuration, formatServings, useT, DIFFICULTY_LABEL_KEYS, type Recipe } from "@reciperoll/core";
import { StarRating } from "./StarRating";

type MetaVisibility = {
  duration: boolean;
  difficulty: boolean;
  servings: boolean;
  rating: boolean;
  tags: boolean;
};

type MetaItem = { icon: React.ReactNode; value: string; label: string };

// Recipe "stats" card: a top row of info columns (time / difficulty / servings)
// over a rating row. Fields are optional (per-user toggles + missing values), so
// the top row is built from whatever's present and the columns flex to fill — one
// item spans the width, two split it, three take thirds — with dividers only
// between them. Mirrors the RN RecipeMetaCard.
export function RecipeMetaCard({
  recipe,
  showMeta,
  onRate,
}: {
  recipe: Recipe;
  showMeta: MetaVisibility;
  onRate: (rating: number | null) => void;
}) {
  const t = useT();
  const items: MetaItem[] = [];
  if (showMeta.duration && recipe.cookingDuration)
    items.push({
      icon: <Clock className="w-4 h-4 text-terracotta" />,
      value: formatDuration(recipe.cookingDuration, t),
      label: t("recipe.totalTime"),
    });
  if (showMeta.difficulty && recipe.difficulty)
    items.push({
      icon: <Layers className="w-4 h-4 text-terracotta" />,
      value: t(DIFFICULTY_LABEL_KEYS[recipe.difficulty]),
      label: t("recipe.difficulty"),
    });
  if (showMeta.servings && recipe.servings)
    items.push({
      icon: <Users className="w-4 h-4 text-terracotta" />,
      value: formatServings(recipe.servings, t) ?? "",
      label: t("recipe.servings"),
    });

  const showRating = showMeta.rating;
  if (items.length === 0 && !showRating) return null;

  return (
    <div className="rounded-2xl border border-line px-3 py-4 mb-6">
      {items.length > 0 && (
        <div className="flex items-stretch">
          {items.map((item, i) => (
            <React.Fragment key={item.label}>
              {i > 0 && <div className="w-px bg-line" />}
              <div className="flex-1 flex flex-col items-center px-2">
                <div className="flex items-center gap-2">
                  {item.icon}
                  <span className="text-base font-bold text-ink">{item.value}</span>
                </div>
                <span className="text-xs text-ink-faint mt-1">{item.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {showRating && (
        <>
          {items.length > 0 && <div className="h-px bg-line my-4" />}
          <div className="flex flex-col items-center">
            <StarRating value={recipe.rating} onChange={onRate} className="w-5 h-5" />
            <span className="text-xs text-ink-faint mt-1.5">
              {recipe.rating ? t("recipe.rating") : t("recipe.rateThisRecipe")}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default RecipeMetaCard;
